import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useUserSettings } from '../hooks/useUserSettings';
import { useNotification } from '../hooks/useNotification';
import { CURRENCY_OPTIONS } from '../utils/money';

// What an amount looks like with a given currency and number format
const preview = (currency, numberFormat, amount) => {
  try {
    return new Intl.NumberFormat(numberFormat === 'international' ? 'en-US' : 'en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (e) {
    return `${currency} ${amount}`;
  }
};

/**
 * Settings → General: the currency every amount is shown in, and how large
 * numbers are grouped.
 */
const GeneralSettings = () => {
  const { settings, loading, update } = useUserSettings();
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ currency: 'INR', numberFormat: 'indian' });

  useEffect(() => {
    if (settings) {
      setForm({ currency: settings.currency || 'INR', numberFormat: settings.numberFormat || 'indian' });
    }
  }, [settings]);

  const save = async (changes) => {
    const next = { ...form, ...changes };
    setForm(next);
    try {
      setSaving(true);
      await update(changes);
      showSuccess('Saved');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save');
      if (settings) setForm({ currency: settings.currency, numberFormat: settings.numberFormat });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title="Currency and number format"
        subheader="Every amount in the app is shown this way"
      />
      <CardContent>
        <Stack spacing={3} sx={{ maxWidth: 480 }}>
          <FormControl fullWidth disabled={saving}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={form.currency}
              label="Currency"
              onChange={(e) => save({ currency: e.target.value })}
            >
              {CURRENCY_OPTIONS.map(option => (
                <MenuItem key={option.code} value={option.code}>
                  {option.code} · {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl disabled={saving}>
            <FormLabel>Number format</FormLabel>
            <RadioGroup
              value={form.numberFormat}
              onChange={(e) => save({ numberFormat: e.target.value })}
            >
              <FormControlLabel
                value="indian"
                control={<Radio />}
                label={`Indian: ${preview(form.currency, 'indian', 1250000)} (lakh and crore)`}
              />
              <FormControlLabel
                value="international"
                control={<Radio />}
                label={`International: ${preview(form.currency, 'international', 1250000)} (thousand and million)`}
              />
            </RadioGroup>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            This changes how amounts are shown, not the amounts themselves. Transactions read from
            your emails and statements are taken as they are.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default GeneralSettings;
